import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useShop } from '../context/ShopContext';
import { X, Sparkles, Tag, Check, ArrowRight, Gift } from 'lucide-react';

export default function PromoPopupModal() {
  const navigate = useNavigate();
  const { 
    promoPopupData, 
    isPromoPopupDismissed, 
    setIsPromoPopupDismissed,
    applyCoupon,
    addToast 
  } = useShop();

  const [copied, setCopied] = useState(false);

  // If disabled or dismissed by patron, do not render
  if (!promoPopupData?.isEnabled || isPromoPopupDismissed) {
    return null;
  }

  const handleDismiss = () => {
    setIsPromoPopupDismissed(true);
    try {
      sessionStorage.setItem('ml_promo_popup_dismissed', 'true');
    } catch (_) {}
  };

  const handleCopyCode = (e) => {
    e.stopPropagation();
    if (!promoPopupData?.couponCode) return;
    navigator.clipboard.writeText(promoPopupData.couponCode);
    setCopied(true);
    addToast(`Coupon "${promoPopupData.couponCode}" copied to clipboard!`, 'success');
    setTimeout(() => setCopied(false), 2500);
  };

  const handleClaimOffer = () => {
    if (promoPopupData?.couponCode) {
      applyCoupon(promoPopupData.couponCode);
    }
    handleDismiss();
    navigate('/category/all');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div 
      className="promo-popup-overlay"
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 99999,
        backgroundColor: 'rgba(5, 5, 10, 0.85)',
        backdropFilter: 'blur(8px)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '0.75rem',
        animation: 'fadeIn 0.25s ease-out'
      }}
      onClick={handleDismiss}
    >
      <div 
        className="promo-popup-card"
        style={{
          width: '100%',
          maxWidth: '430px',
          maxHeight: '88vh',
          backgroundColor: '#11111a',
          border: '1.5px solid var(--border-gold)',
          borderRadius: '16px',
          overflowY: 'auto',
          boxShadow: '0 25px 60px rgba(0, 0, 0, 0.95), 0 0 30px rgba(212, 175, 55, 0.25)',
          position: 'relative',
          animation: 'popupScaleIn 0.3s cubic-bezier(0.16, 1, 0.3, 1)'
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Floating Close Button */}
        <button
          onClick={handleDismiss}
          type="button"
          style={{
            position: 'absolute',
            top: '10px',
            right: '10px',
            width: '34px',
            height: '34px',
            borderRadius: '50%',
            backgroundColor: 'rgba(10, 10, 15, 0.88)',
            backdropFilter: 'blur(6px)',
            border: '1.5px solid var(--gold-400)',
            color: 'var(--gold-300)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            zIndex: 30,
            boxShadow: '0 2px 10px rgba(0, 0, 0, 0.8)',
            transition: 'all 0.2s'
          }}
          aria-label="Close promotion"
        >
          <X size={18} strokeWidth={2.5} />
        </button>

        {/* Banner Image Container */}
        {promoPopupData?.bannerImg ? (
          <div 
            className="promo-popup-img-container"
            style={{
              position: 'relative',
              width: '100%',
              height: '170px',
              backgroundColor: '#0a0a0f',
              overflow: 'hidden',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <img 
              src={promoPopupData.bannerImg} 
              alt={promoPopupData.title || 'Promotional Offer'}
              style={{
                width: '100%',
                height: '100%',
                objectFit: promoPopupData.imageFit === 'contain' ? 'contain' : 'cover',
                display: 'block'
              }}
            />
            {/* Gradient shadow for seamless blend */}
            <div style={{
              position: 'absolute',
              inset: 0,
              background: 'linear-gradient(to bottom, transparent 40%, #11111a 100%)',
              pointerEvents: 'none'
            }} />

            {/* Discount Badge */}
            {promoPopupData.discountBadge && (
              <div style={{
                position: 'absolute',
                top: '10px',
                left: '10px',
                background: 'var(--gold-gradient)',
                color: '#0a0a0f',
                fontSize: '0.68rem',
                fontWeight: 800,
                padding: '0.2rem 0.6rem',
                borderRadius: '999px',
                letterSpacing: '0.04em',
                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.6)'
              }}>
                {promoPopupData.discountBadge}
              </div>
            )}
          </div>
        ) : (
          <div style={{
            background: 'radial-gradient(ellipse at 50% 40%, rgba(212, 175, 55, 0.25) 0%, #11111a 75%)',
            padding: '1.4rem 1rem 0.5rem',
            textAlign: 'center'
          }}>
            <div style={{
              width: '46px',
              height: '46px',
              borderRadius: '50%',
              background: 'var(--gold-gradient)',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              boxShadow: 'var(--shadow-gold)',
              marginBottom: '0.4rem'
            }}>
              <Gift size={22} color="#0a0a0f" />
            </div>
          </div>
        )}

        {/* Modal Body Content */}
        <div 
          className="promo-popup-body"
          style={{ padding: '1.1rem 1.4rem 1.4rem', textAlign: 'center' }}
        >
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.35rem',
            color: 'var(--gold-400)',
            fontSize: '0.68rem',
            fontWeight: 700,
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
            marginBottom: '0.3rem'
          }}>
            <Sparkles size={11} /> Limited Festive Privilege
          </div>

          <h3 
            className="promo-popup-title"
            style={{
              fontSize: '1.35rem',
              fontWeight: 800,
              color: '#fff',
              marginBottom: '0.35rem',
              lineHeight: 1.25
            }}
          >
            {promoPopupData.title || 'Exclusive Concession Offer'}
          </h3>

          <p 
            className="promo-popup-subtitle"
            style={{
              color: 'var(--text-secondary)',
              fontSize: '0.82rem',
              lineHeight: 1.45,
              marginBottom: '1rem',
              maxWidth: '340px',
              marginInline: 'auto'
            }}
          >
            {promoPopupData.subtitle || 'Unlock royal pricing on certified natural diamond & pure 22K gold heirlooms.'}
          </p>

          {/* Coupon Code Voucher Pill */}
          {promoPopupData.couponCode && (
            <div 
              className="promo-popup-coupon"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '0.65rem',
                backgroundColor: 'rgba(212, 175, 55, 0.1)',
                border: '1px dashed var(--gold-400)',
                borderRadius: '8px',
                padding: '0.45rem 0.85rem',
                marginBottom: '1rem'
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '0.35rem' }}>
                <Tag size={13} color="var(--gold-400)" />
                <span style={{
                  color: 'var(--gold-300)',
                  fontWeight: 800,
                  fontSize: '0.98rem',
                  letterSpacing: '0.08em',
                  fontFamily: 'monospace'
                }}>
                  {promoPopupData.couponCode}
                </span>
              </div>

              <button
                type="button"
                onClick={handleCopyCode}
                style={{
                  background: copied ? '#34d399' : 'rgba(255, 255, 255, 0.12)',
                  border: 'none',
                  color: copied ? '#0a0a0f' : '#fff',
                  fontSize: '0.68rem',
                  fontWeight: 700,
                  padding: '0.25rem 0.55rem',
                  borderRadius: '5px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.2rem',
                  transition: 'all 0.2s'
                }}
              >
                {copied ? <Check size={11} /> : null}
                <span>{copied ? 'COPIED' : 'COPY'}</span>
              </button>
            </div>
          )}

          {/* Action CTA Button */}
          <button
            type="button"
            onClick={handleClaimOffer}
            style={{
              width: '100%',
              padding: '0.75rem 1rem',
              background: 'var(--gold-gradient)',
              color: '#0a0a0f',
              border: 'none',
              borderRadius: '8px',
              fontSize: '0.88rem',
              fontWeight: 800,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '0.45rem',
              boxShadow: 'var(--shadow-gold)',
              transition: 'all 0.2s'
            }}
          >
            <span>{promoPopupData.btnText || 'Claim Offer & Shop Now'}</span>
            <ArrowRight size={15} />
          </button>
        </div>
      </div>

      <style>{`
        @keyframes popupScaleIn {
          from {
            opacity: 0;
            transform: scale(0.92) translateY(10px);
          }
          to {
            opacity: 1;
            transform: scale(1) translateY(0);
          }
        }

        /* Mobile specific styles to make popup compact and perfectly fit */
        @media (max-width: 640px) {
          .promo-popup-card {
            max-width: 340px !important;
            max-height: 84vh !important;
            border-radius: 14px !important;
          }
          .promo-popup-img-container {
            height: 125px !important;
          }
          .promo-popup-body {
            padding: 0.85rem 1rem 1rem !important;
          }
          .promo-popup-title {
            font-size: 1.15rem !important;
            margin-bottom: 0.25rem !important;
          }
          .promo-popup-subtitle {
            font-size: 0.74rem !important;
            line-height: 1.35 !important;
            margin-bottom: 0.75rem !important;
          }
          .promo-popup-coupon {
            padding: 0.35rem 0.65rem !important;
            margin-bottom: 0.8rem !important;
          }
        }
      `}</style>
    </div>
  );
}
